<script>
/**
 * @component MunicipalCredentialCard
 * @description UC-REG-295 — Banner / tarjeta de la credencial municipal
 *              (Campana): logo institucional, nombre del municipio, leyenda
 *              del tenant ("Salud Campana"), Nombre Apellido (en ese orden) y
 *              "DNI" + número. Sin token, número de afiliado ni plan. Se usa
 *              como banner en el Home del paciente (`compact`) y como tarjeta
 *              principal en Mi credencial.
 * @example <MunicipalCredentialCard :person="credential" compact />
 */
import logo from "@/mixins/logo";
import {
  formatDni,
  getMunicipalCredentialBranding,
} from "@/services/credential";

export default {
  name: "MunicipalCredentialCard",

  mixins: [logo],

  props: {
    /** @type {{fullName: string, dni: string|null}} salida de `toMunicipalCredential` */
    person: { type: Object, default: null },
    /** @type {boolean} Versión banner (Home): más baja, con flecha */
    compact: { type: Boolean, default: false },
    /** @type {string} Etiqueta extra (ej. parentesco de un integrante) */
    tag: { type: String, default: "" },
  },

  computed: {
    /** @returns {{brandName: string, organizationName: string}} */
    branding() {
      return getMunicipalCredentialBranding();
    },

    /** @returns {string} */
    fullName() {
      return (this.person && this.person.fullName) || "—";
    },

    /** @returns {string} DNI formateado */
    dni() {
      return formatDni(this.person && this.person.dni);
    },

    /**
     * @description Logo de la tarjeta: la variante `-credential` del theme
     *              (ya en blanco) o, si no existe, el logo alternativo pintado
     *              en blanco.
     * @returns {{src: string, inverted: boolean}|null}
     */
    logo() {
      if (this.logoCredentialUrl) {
        return { src: this.logoCredentialUrl, inverted: false };
      }
      if (this.logoAltUrl) {
        return { src: this.logoAltUrl, inverted: true };
      }
      return null;
    },
  },
};
</script>

<template lang="pug">
.mcc(:class="{ 'mcc--compact': compact }" @click="$emit('click')")
  .mcc__header
    img.mcc__logo(
      v-if="logo"
      :src="logo.src"
      :class="{ 'mcc__logo--inverted': logo.inverted }"
      :alt="branding.organizationName || 'Logo'"
    )
    span.mcc__organization(v-else-if="branding.organizationName") {{ branding.organizationName }}
    span.mcc__brand {{ branding.brandName }}
  .mcc__body
    .mcc__person
      span.mcc__name {{ fullName }}
      span.mcc__dni
        span.mcc__dni-label DNI
        span.mcc__dni-value {{ dni }}
      span.mcc__tag(v-if="tag") {{ tag }}
    micon.mcc__arrow(v-if="compact" name="chevron_right")
</template>

<style lang="scss" scoped>
.mcc {
  background: linear-gradient(
    135deg,
    var(--org-color-accent, $primary-alt) 0%,
    var(--org-color-primary, $primary) 55%,
    $secondary 100%
  );
  border-radius: 16px;
  padding: 22px 24px;
  color: #fff;
  position: relative;
  overflow: hidden;
  margin-bottom: 16px;

  &::after {
    content: "";
    position: absolute;
    top: -50px;
    right: -50px;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
  }

  &__logo {
    height: auto;
    max-height: 44px;
    max-width: 55%;
    object-fit: contain;
    object-position: left center;

    &--inverted {
      filter: brightness(0) invert(1);
    }
  }

  &__organization {
    font-size: 14px;
    font-weight: 700;
  }

  &__brand {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.18);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
    white-space: nowrap;
    backdrop-filter: blur(4px);
  }

  &__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__person {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__name {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dni {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 1px;
    font-family: "Plus Jakarta Sans", monospace;
  }

  &__dni-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    opacity: 0.75;
  }

  &__tag {
    display: inline-flex;
    align-self: flex-start;
    margin-top: 6px;
    padding: 3px 10px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 600;
  }

  &__arrow {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    fill: rgba(255, 255, 255, 0.6);
  }

  // ─── Banner del Home ───
  &--compact {
    padding: 16px 20px;
    border-radius: 12px;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    }

    .mcc__header {
      margin-bottom: 12px;
    }

    .mcc__logo {
      max-height: 32px;
    }

    .mcc__name {
      font-size: 17px;
    }

    .mcc__dni {
      font-size: 13px;
    }
  }
}

@media (max-width: 767px) {
  .mcc {
    padding: 18px 16px;
    border-radius: 12px;

    &__logo {
      max-height: 34px;
      max-width: 50%;
    }

    &__brand {
      font-size: 11px;
      padding: 4px 10px;
    }

    &__name {
      font-size: 17px;
    }
  }
}
</style>
